import { useCallback, useEffect, useRef, useState } from 'react';

import { matchPronunciation } from '../utils/pronunciationMatch';

const LISTEN_TIMEOUT_MS = 8000;
const FINAL_GRACE_MS = 1200;

const getRecognition = () => (typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition || null
  : null);

const NO_SPEECH_TEXT = 'Ми вас не почули. Натисніть на мікрофон і скажіть слово ближче до мікрофона.';

const ERROR_TEXT = {
  'not-allowed': 'Немає доступу до мікрофона. Дозвольте мікрофон для сайту в налаштуваннях браузера і спробуйте ще раз.',
  'service-not-allowed': 'Браузер не дозволив розпізнавання мовлення. Спробуйте в Chrome, Edge або Safari.',
  'audio-capture': 'Мікрофон не знайдено. Перевірте, чи він підключений і дозволений.',
  network: 'Не вдалося зв’язатися із сервісом розпізнавання. Перевірте інтернет; у Brave, Arc і Opera розпізнавання '
    + 'часто вимкнене — відкрийте сторінку в Chrome, Edge або Safari.',
  'no-speech': NO_SPEECH_TEXT,
  aborted: '',
  'language-not-supported': 'Цей браузер не розпізнає мову курсу.',
};

let audioContext = null;

const tone = (ctx, frequency, start, duration, type = 'sine', gain = 0.18) => {
  const osc = ctx.createOscillator();
  const amp = ctx.createGain();
  osc.type = type;
  osc.frequency.value = frequency;
  amp.gain.setValueAtTime(0.0001, start);
  amp.gain.exponentialRampToValueAtTime(gain, start + 0.02);
  amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(amp);
  amp.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.05);
};

const ensureAudio = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioContext) audioContext = new Ctx();
  if (audioContext.state === 'suspended') audioContext.resume();

  return audioContext;
};

export const primeVerdictSound = () => {
  try {
    ensureAudio();
  } catch (e) {
    audioContext = null;
  }
};

export const playVerdictSound = (verdict) => {
  try {
    const ctx = ensureAudio();
    if (!ctx) return;
    const now = ctx.currentTime;
    if (verdict === 'start') {
      tone(ctx, 880, now, 0.08, 'sine', 0.12);
    } else if (verdict === 'ok') {
      tone(ctx, 660, now, 0.14);
      tone(ctx, 990, now + 0.15, 0.22);
    } else if (verdict === 'close') {
      tone(ctx, 520, now, 0.18, 'triangle');
    } else {
      tone(ctx, 220, now, 0.28, 'triangle', 0.14);
    }
  } catch (e) {
    audioContext = null;
  }
};

const collectTranscripts = (results) => {
  const finals = [];
  const interims = [];
  for (let i = 0; i < results.length; i += 1) {
    const bucket = results[i].isFinal === false ? interims : finals;
    for (let k = 0; k < results[i].length; k += 1) {
      const text = results[i][k]?.transcript;
      if (text && text.trim()) bucket.push(text);
    }
  }

  return { finals, interims };
};

const useSpeechRecognition = (lang) => {
  const Recognition = getRecognition();
  const isSupported = Boolean(Recognition);
  const [listeningId, setListeningId] = useState(null);
  const [isMicOn, setIsMicOn] = useState(false);
  const activeRef = useRef(null);
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    clearTimeout(timerRef.current);
    const active = activeRef.current;
    activeRef.current = null;
    if (active) {
      try {
        active.abort();
      } catch (e) {
        activeRef.current = null;
      }
    }
    setListeningId(null);
    setIsMicOn(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const listen = useCallback((item) => new Promise((resolve) => {
    if (!Recognition) {
      resolve({ verdict: 'unsupported', heard: '', score: 0, message: '' });

      return;
    }
    stop();

    const recognition = new Recognition();
    recognition.lang = lang || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 5;

    let settled = false;
    let lastInterims = [];
    let graceTimer = null;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(graceTimer);
      if (activeRef.current === recognition) {
        clearTimeout(timerRef.current);
        activeRef.current = null;
        setListeningId(null);
        setIsMicOn(false);
      }
      resolve(result);
    };

    const finishWith = (transcripts) => {
      if (!transcripts.length) {
        finish({ verdict: 'none', heard: '', score: 0, message: NO_SPEECH_TEXT });

        return;
      }
      finish({ ...matchPronunciation(item.word, transcripts), message: '' });
    };

    recognition.onresult = (event) => {
      const { finals, interims } = collectTranscripts(event.results);
      if (finals.length) {
        finishWith(finals);

        return;
      }
      if (interims.length) lastInterims = interims;
      clearTimeout(graceTimer);
      graceTimer = setTimeout(() => finishWith(lastInterims), FINAL_GRACE_MS);
    };
    recognition.onerror = (event) => {
      const code = event?.error || 'unknown';
      if (code === 'no-speech' && lastInterims.length) {
        finishWith(lastInterims);

        return;
      }
      finish({
        verdict: code === 'no-speech' ? 'none' : 'error',
        heard: '',
        score: 0,
        message: ERROR_TEXT[code] ?? 'Не вдалося розпізнати. Спробуйте ще раз.',
      });
    };
    recognition.onend = () => finishWith(lastInterims);

    const armTimeout = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          recognition.stop();
        } catch (e) {
          finishWith(lastInterims);
        }
      }, LISTEN_TIMEOUT_MS);
    };

    let micAnnounced = false;
    const announceMic = () => {
      if (micAnnounced || activeRef.current !== recognition) return;
      micAnnounced = true;
      setIsMicOn(true);
      playVerdictSound('start');
      armTimeout();
    };
    recognition.onaudiostart = announceMic;
    recognition.onstart = announceMic;

    activeRef.current = recognition;
    setListeningId(item.id);
    setIsMicOn(false);
    armTimeout();

    try {
      recognition.start();
    } catch (e) {
      finish({
        verdict: 'error',
        heard: '',
        score: 0,
        message: 'Мікрофон уже використовується. Зачекайте секунду і спробуйте ще раз.',
      });
    }
  }), [Recognition, lang, stop]);

  return {
    isSupported,
    listeningId,
    isMicOn,
    listen,
    stop,
  };
};

export default useSpeechRecognition;
